import { reactive, getCurrentInstance, computed, ref, onMounted } from "vue";
import { useFilters } from "@/composables/filters/useFilters";
import { useReadinessEmployees } from "@/composables/readinessEmployees/useReadinessEmployees";
import { useEmployees } from "@/composables/employees/useEmployees";
import { transformChatterData } from "@/utils/chatterUtils";
import { getDefaultFiltersForKey } from "@components/constants/filterDefaultsRegistry";
import { getListOfDepartments } from "@/api";
import { mapApiDepartmentsToPickerList } from "@components/DepartmentPickerField/departmentPickerFieldUtils.js";
import { LD_FLAG_IDEA_MDP_552 } from "@components/constants/launchDarklyFlagKeys";
import {
  buildExcludeFromAvgPayload,
  excludeFromAvgToApi,
  excludeFromAvgToValues,
  getReadinessEmployeeFilterLabel,
  getReadinessEmployeeFilterValueFromLabel,
} from "@components/FilterEmployeesReadinessModal/employeeReadinessFilterPayload";

// Create a singleton instance to ensure state consistency
let instance = null;

export function useReadinessEmployeeViewState() {
  if (instance) return instance;
  const filter = useFilters();
  const employees = useReadinessEmployees();
  const employee = useEmployees();
  const app = getCurrentInstance();
  const { proxy } = app || {};

  // Load saved filters from localStorage (falls back to registry defaults)
  const savedFilters = filter.getFilters("readinessEmployees");
  const defaultFilters = getDefaultFiltersForKey("readinessEmployees");

  const state = reactive({
    filters: {
      values: Array.isArray(savedFilters) ? savedFilters : [...defaultFilters],
      searchText: "",
      showFilterDialog: false,
      key: "readinessEmployees",
      clearSelection: "",
      filterGroups: [
        {
          label: "Recalculation",
          items: [
            { label: "No Filter", value: "no_filter" },
            { label: "Requires Recalc", value: "requires_recalc" },
          ],
        },
      ],
      needsRecalculation: false,
      // ER-10506 / MDP-552 (Rules Rule 5): Excluded (exclude_from_avg) radio + Department
      // picker state. Department lives in-memory only (mirrors the Organization > Employees
      // MDP-440 filter bar); the Excluded selection is persisted as a slug in `values`.
      department: null,
      departmentScope: "subtree",
      departments: [],
      departmentsLoading: false,
    },
    loading: {
      table: false,
    },
    openChatterModal: {
      show: false,
      chatterData: [],
      totalEmployeeChatter: 0,
      page: 1,
    },
    table: {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: [],
      sortDesc: [],
      search: "",
    },
    readiness: {
      average_readiness: 0,
      loading: false,
    },
    featureFlags: {
      employeeAvailabilitySelector: true, // Default value
    },
  });

  const resetPagination = () => {
    state.table.currentPage = 1;
  };

  // ER-10506 / MDP-552 (Rules Rule 5): when enabled the Employees filter bar drops the
  // Recalculation radio and uses the Excluded + Department modal; when off the legacy
  // Recalculation-only SearchModal filter bar is preserved.
  const isReadinessFilterBarMdp552Enabled = ref(false);

  // Initialize view state - reset pagination and search on component mount
  const initializeViewState = () => {
    resetPagination();
    clearSearchText();
  };

  const applyFilters = async (filters) => {
    const newArray = filters
      .map((item) => {
        if (item.value !== null) {
          if (typeof item === "string") {
            return item;
          } else if (
            typeof item === "object" &&
            // eslint-disable-next-line no-prototype-builtins
            item.hasOwnProperty("value")
          ) {
            return item.value;
          }
        }
      })
      .filter(Boolean);
    filter.setFilters({ key: state.filters.key, filters: newArray });
    state.filters.values = newArray;
    state.filters.showFilterDialog = false;

    // Reset pagination when filters are applied
    resetPagination();
    await loadEmployeesWithFilters();
  };

  const removeFilters = async (removedFilters) => {
    // Legacy filter bar (idea-MDP-552 off): keep the historical behaviour — persist the
    // remaining values and surface the removed one through SearchModal's clearSelection.
    if (!isReadinessFilterBarMdp552Enabled.value) {
      filter.setFilters({
        key: state.filters.key,
        filters: state.filters.values,
      });
      state.filters.clearSelection = removedFilters;
      return;
    }

    // ER-10506 / MDP-552: the SearchBar chip removes a persisted applied-filter value by
    // label — drop it from `values`, persist, and reload the table immediately.
    const removedValue = getReadinessEmployeeFilterValueFromLabel(
      typeof removedFilters === "object" ? removedFilters.value : removedFilters
    );
    state.filters.values = (state.filters.values || []).filter((value) => value !== removedValue);
    filter.setFilters({
      key: state.filters.key,
      filters: state.filters.values,
    });
    state.filters.clearSelection = removedValue;
    resetPagination();
    await loadEmployeesWithFilters();
  };
  // ER-10506 / MDP-552 (Rules Rule 5): translate the applied filters into Employees v2
  // list query params. The new Excluded radio + Department picker are used only while the
  // idea-MDP-552 flag is on; otherwise the legacy Recalculation SearchModal behaviour is
  // preserved. Shared by the table reload and the "Recalculate All" reload so the applied
  // filter set is never dropped by the recalculation path.
  const addReadinessFilterQueryParams = (queryParams) => {
    if (isReadinessFilterBarMdp552Enabled.value) {
      Object.assign(queryParams, excludeFromAvgToApi(state.filters.values));
      if (state.filters.department?.id != null) {
        queryParams.department = state.filters.department.id;
        queryParams.department_scope = state.filters.departmentScope || "subtree";
      }
    } else if (state.filters.values.includes("requires_recalc")) {
      queryParams.needs_recalc = true;
    }
  };

  const recalculateAllEmployees = async () => {
    proxy.$loader.show();

    // Update the employee store's pagination state to match our viewState BEFORE making API call
    if (employees.employeeStore) {
      employees.employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employees.employeeStore.getTotalEmployeesCount,
      });
    }

    // Build query parameters to preserve current state
    const queryParams = {};

    // Add pagination parameters
    queryParams.limit = state.table.itemsPerPage;
    queryParams.offset = (state.table.currentPage - 1) * state.table.itemsPerPage;

    // Add search parameter for first_name
    if (state.filters.searchText && state.filters.searchText.trim()) {
      queryParams.first_name = state.filters.searchText.trim();
    }

    // Add filter parameters
    addReadinessFilterQueryParams(queryParams);

    // Add ordering
    queryParams.ordering = "first_name";

    // Add status filter to only get active employees
    queryParams.status = "active";

    // Add archived filter to exclude archived employees
    queryParams.archived = false;

    await employees.recalculateEmployees(queryParams);

    proxy.$loader.hide();
  };

  const currentEmployee = computed(
    () => state.assignmentWizard.currentEmployee || state.relieveAssignment.currentEmployee
  );

  const getEmployeeCardProps = (employee) => {
    // Check feature flag for availability selector
    const isAvailabilitySelectorEnabled = state.featureFlags.employeeAvailabilitySelector;

    const hideAvailabilitySelector =
      !isAvailabilitySelectorEnabled || employee?.primary_position || false;

    return {
      firstName: employee.first_name,
      lastName: employee.last_name,
      position: employee?.primary_position ? employee.primary_position.name : "",
      // position: employee.title,
      photoUrl: employee.avatar,
      availability: employee.availability,
      showAvailability: true,
      nameUrl: `/organization/employees/${employee.id}/pathtoreadiness`,
      showLinkUnderline: false,
      hideAvailabilitySelector: hideAvailabilitySelector,
      isAllocated: employee?.primary_position || false,
    };
  };
  const getEmployeeReadinessProps = (employee) => {
    if (employee.needs_recalc) {
      state.filters.needsRecalculation = true;
    }
    return {
      recalc: employee.needs_recalc,
      readiness: employee.current_readiness,
      status: "Has Assignments",
      name: employee.primary_position?.name,
      code: employee.primary_position?.code_str?.split(" ")[0] || null,
    };
  };
  const getEmployeePrimaryReadinessProps = (employee) => {
    return {
      recalc: false,
      readiness: employee.primary_readiness,
      status: getEmployeePrimaryReadinessStatus(employee),
      name: employee.primary_position?.name,
      code: employee.primary_position?.code_str?.split(" ")[0] || null,
      showImportance: true,
      importance: employee.primary_importance,
    };
  };
  const getEmployeePrimaryReadinessStatus = (employee) => {
    if (!employee.primary_position) {
      return "No Assignments";
    } else {
      return "Has Assignments";
    }
  };
  const getEmployeePrepareReadinessProps = (employee) => {
    const firstPreparingPosition = employee.preparing_for_positions?.[0];
    return {
      recalc: false,
      readiness: employee.preparing_for_readiness,
      status: getEmployeePrepareReadinessStatus(employee),
      name: firstPreparingPosition?.name,
      code: firstPreparingPosition?.code_str?.split(" ")[0] || null,
      showImportance: true,
      importance: employee.preparing_for_importance,
    };
  };
  const getEmployeePrepareReadinessStatus = (employee) => {
    if (!employee.preparing_for_positions || employee.preparing_for_positions.length === 0) {
      return "No Assignments";
    } else {
      return "Has Assignments";
    }
  };
  const getSwitcherProps = (employee) => {
    return {
      label: "",
      value: employee.exclude_from_avg,
      left: "-25px",
      toggleLoader: state.readiness.loading,
    };
  };

  // Métodos para filtros
  const updateFilter = (filterName, value) => {
    if (filterName in state.filters) {
      state.filters[filterName] = value;
    }
  };

  const clearFilters = async () => {
    // ER-11195: "Clear all" restores the documented defaults from the central
    // registry instead of resetting the whole filters object to a legacy shape.
    state.filters.values = filter.resetToDefaults(state.filters.key);
    state.filters.showFilterDialog = false;

    // ER-10506 / MDP-552: clearing all filters also drops the in-memory Department
    // selection and reloads the table so the cleared state is reflected immediately.
    if (isReadinessFilterBarMdp552Enabled.value) {
      state.filters.department = null;
      state.filters.departmentScope = "subtree";
      resetPagination();
      await loadEmployeesWithFilters();
    }
  };

  const clearSearchText = () => {
    state.filters.searchText = "";
  };

  // Métodos para loading
  const setLoading = (key, value) => {
    if (key in state.loading) {
      state.loading[key] = value;
    }
  };
  const getEmployeeChatter = async () => {
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      state.openChatterModal.show = true;
      const response = await employee.employeeChatter(
        accountID,
        "employee",
        state.openChatterModal.page
      );
      if (response) {
        const chatterItems = transformChatterData(response.data.results);
        state.openChatterModal.chatterData.push(...chatterItems);
        state.openChatterModal.totalEmployeeChatter = response.data.count;
      }
    } catch (error) {
      console.log("e: ", error);
    }
  };
  const openEmployeeChatterModal = async () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.show = true;
    await getEmployeeChatter();
  };
  const closeEmployeeChatterModal = () => {
    state.openChatterModal.chatterData = [];
    state.openChatterModal.totalEmployeeChatter = 0;
    state.openChatterModal.page = 1;
    state.openChatterModal.show = false;
  };
  const requestMoreChatter = () => {
    const page = Math.ceil(state.openChatterModal.chatterData.length / 10);
    state.openChatterModal.page = page + 1;
    getEmployeeChatter();
  };
  const loadAccountDetails = async () => {
    state.readiness.loading = true;
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      const response = await employee.getAccountDetails(accountID);
      if (response) {
        setTimeout(() => {
          state.readiness.average_readiness = response.data.avg_employee_readiness;
          state.readiness.loading = false;
        }, 1500);
      } else {
        state.readiness.loading = false;
      }
    } catch (error) {
      console.error("Error loading account details:", error);
      state.readiness.loading = false;
    }
  };

  const handleTablePageChange = async (page) => {
    state.table.currentPage = page;
    await employees.loadEmployeesPage(page, state.table.itemsPerPage);
  };

  const handleTableItemsPerPageChange = async (itemsPerPage) => {
    state.table.itemsPerPage = itemsPerPage;
    state.table.currentPage = 1; // Reset to first page
    await employees.loadEmployeesPage(1, itemsPerPage);
  };

  // Debounce function for search
  const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  // Perform search with proper filtering
  const performSearch = async (searchText) => {
    // Always clear existing search filters first
    if (state.filters.searchText) {
      // Clear previous search
      state.filters.searchText = "";
    }

    // Only add search filters and load employees if there's actually a search term
    if (searchText && searchText.trim() !== "") {
      state.filters.searchText = searchText.trim();
    }

    // Reset to first page when searching
    resetPagination();

    // Load employees with current filters (including search)
    await loadEmployeesWithFilters();
  };

  // Debounced search function
  const debouncedSearch = debounce(performSearch, 500);

  // Handle search input
  const handleSearch = (searchText) => {
    debouncedSearch(searchText);
  };

  // Load employees with current filters
  const loadEmployeesWithFilters = async () => {
    // Update the employee store's pagination state to match our viewState BEFORE making API call
    const employeeStore = employees.employeeStore;
    if (employeeStore) {
      employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employeeStore.getTotalEmployeesCount,
      });
    }

    const queryParams = {};

    // Add pagination parameters
    queryParams.limit = state.table.itemsPerPage;
    queryParams.offset = (state.table.currentPage - 1) * state.table.itemsPerPage;

    // Add search parameter for first_name
    if (state.filters.searchText && state.filters.searchText.trim()) {
      queryParams.name = state.filters.searchText.trim();
    }

    // Add filter parameters
    addReadinessFilterQueryParams(queryParams);

    // Add ordering
    queryParams.ordering = "first_name";

    // Add status filter to only get active employees
    queryParams.status = "active";

    // Add archived filter to exclude archived employees
    queryParams.archived = false;

    await employees.loadAllEmployees(queryParams);
  };

  // ER-10506 / MDP-552 (Rules Rule 5): keep the persisted `values` list in sync with the
  // new modal. Only the Excluded radio persists a slug in `values` (Recalculation was
  // removed); the Department selection is held in-memory on `state.filters`.
  const applyReadinessFilterModal = async (payload = {}) => {
    const newValues = excludeFromAvgToValues({ excluded: payload.excluded });
    state.filters.department = payload.department || null;
    state.filters.departmentScope = payload.department ? payload.scope || "subtree" : "subtree";

    filter.setFilters({ key: state.filters.key, filters: newValues });
    state.filters.values = newValues;
    state.filters.showFilterDialog = false;

    resetPagination();
    await loadEmployeesWithFilters();
  };

  const setDepartmentFilter = (department, scope = "subtree") => {
    state.filters.department = department || null;
    state.filters.departmentScope = department ? scope || "subtree" : "subtree";
  };

  /** Type-to-search department list backing the modal's DepartmentPickerField. */
  const loadDepartments = async (query = "") => {
    state.filters.departmentsLoading = true;
    try {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      if (!accountID) {
        state.filters.departments = [];
        return;
      }
      const { data } = await getListOfDepartments(
        accountID,
        query || "",
        { archived: false },
        { limit: 500, offset: 0 }
      );
      const results = Array.isArray(data) ? data : data?.results || [];
      state.filters.departments = mapApiDepartmentsToPickerList(results);
    } catch (error) {
      console.error("Error loading departments for readiness employee filter:", error);
      state.filters.departments = [];
    } finally {
      state.filters.departmentsLoading = false;
    }
  };

  /** In-memory Department-scope toggle from the SearchBar department tag. */
  const toggleDepartmentScope = async () => {
    const newScope = state.filters.departmentScope === "direct" ? "subtree" : "direct";
    setDepartmentFilter(state.filters.department, newScope);
    resetPagination();
    await loadEmployeesWithFilters();
  };

  /** In-memory Department removal from the SearchBar department tag. */
  const clearDepartmentFilter = async () => {
    setDepartmentFilter(null);
    resetPagination();
    await loadEmployeesWithFilters();
  };

  /** Display labels for the SearchBar applied-filter chips (new filter bar only). */
  const getFilterDisplayLabels = (values) =>
    (values || []).map((value) => getReadinessEmployeeFilterLabel(value));

  /** label/value -> persisted slug for chip removal. */
  const getFilterValueFromLabel = (labelOrValue) =>
    getReadinessEmployeeFilterValueFromLabel(labelOrValue);

  /** Modal initial Excluded selection derived from the persisted values. */
  const getInitialExcluded = () => buildExcludeFromAvgPayload(state.filters.values).excluded;
  const updateEmployeeExcludeFromAvgValue = async (item, val) => {
    // Get the actual current page from the store since view state might be reset
    const actualCurrentPage = employees.employeeStore.getPagination.currentPage;
    const actualItemsPerPage =
      employees.employeeStore.getPagination.itemsPerPage || state.table.itemsPerPage;

    // Update view state to match store
    state.table.currentPage = actualCurrentPage;
    state.table.itemsPerPage = actualItemsPerPage;

    state.readiness.loading = true;
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    const payload = {
      emp_id: item.id,
      value: val.value,
    };
    await employee.updateEmployeeExcludeFromAvg(accountID, payload);

    // Update the employee store's pagination state to match our viewState before loading
    if (employees.employeeStore) {
      employees.employeeStore.setPagination({
        currentPage: state.table.currentPage,
        itemsPerPage: state.table.itemsPerPage,
        totalCount: employees.employeeStore.getTotalEmployeesCount,
      });
    }

    // Use loadEmployeesWithFilters to preserve query parameters
    await loadEmployeesWithFilters();
    await loadAccountDetails();
  };

  // Initialize feature flags
  const initializeFeatureFlags = async () => {
    const employeeAvailabilitySelector = await proxy.$getFlag(
      "employee_availability_selector",
      true
    );
    state.featureFlags.employeeAvailabilitySelector = employeeAvailabilitySelector;

    // ER-10506 / MDP-552: new Excluded + Department Employees filter bar (Rule 5).
    isReadinessFilterBarMdp552Enabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_552, false)) === true;
  };

  // Load feature flags on mount
  onMounted(async () => {
    await initializeFeatureFlags();
  });

  const composableInstance = {
    state,
    currentEmployee,
    updateFilter,
    clearFilters,
    setLoading,
    getEmployeeCardProps,
    getEmployeeReadinessProps,
    getEmployeePrimaryReadinessProps,
    getEmployeePrepareReadinessProps,
    applyFilters,
    removeFilters,
    recalculateAllEmployees,
    openEmployeeChatterModal,
    closeEmployeeChatterModal,
    requestMoreChatter,
    getSwitcherProps,
    loadAccountDetails,
    updateEmployeeExcludeFromAvgValue,
    handleTablePageChange,
    handleTableItemsPerPageChange,
    handleSearch,
    loadEmployeesWithFilters,
    initializeFeatureFlags,
    initializeViewState,
    clearSearchText,
    isReadinessFilterBarMdp552Enabled,
    applyReadinessFilterModal,
    setDepartmentFilter,
    loadDepartments,
    toggleDepartmentScope,
    clearDepartmentFilter,
    getFilterDisplayLabels,
    getFilterValueFromLabel,
    getInitialExcluded,
  };

  instance = composableInstance;
  return composableInstance;
}

export function resetReadinessEmployeeViewState() {
  instance = null;
}
